import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import SuperadminLayout from '../../components/SuperadminLayout';
import useAuthStore from '../../store/authStore';
import { Search, Store, LogIn, Loader2, Crown, Wallet, ChefHat, X } from 'lucide-react';

const PLAN_LABEL = { free: 'Pulsuz', basic: 'Sadə', pro: 'Pro' };
const PLAN_TONE = {
    free: 'bg-slate-100 text-slate-600',
    basic: 'bg-blue-50 text-blue-700',
    pro: 'bg-emerald-50 text-emerald-700',
};
const initials = (name) => String(name || '?').trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() || '').join('') || '?';

export default function Impersonate() {
    const navigate = useNavigate();
    const { impersonate } = useAuthStore();
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [busyId, setBusyId] = useState(null);
    // Giriş növü seçimi: { tenant, kassirs, kitchens }
    const [pick, setPick] = useState(null);
    const [entering, setEntering] = useState('');

    useEffect(() => {
        api.get('/superadmin/tenants')
            .then(res => setTenants(res.data.data || []))
            .catch(() => toast.error('Restoranlar yüklənmədi.'))
            .finally(() => setLoading(false));
    }, []);

    // «Daxil ol» → hesabları yüklə və seçim pəncərəsini aç (admin / kassir / mətbəx)
    const enter = async (t) => {
        setBusyId(t.id);
        try {
            const r = await api.get(`/auth/impersonate/${t.id}/accounts`);
            const kassirs = r.data?.kassirs || [];
            const kitchens = r.data?.kitchens || [];
            // Yalnız admin girişi varsa → pəncərə açma, birbaşa admin kimi gir
            if (!kassirs.length && !kitchens.length) { await enterAs('admin', null, 'admin', t); return; }
            setPick({ tenant: t, kassirs, kitchens });
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Hesablar yüklənmədi.');
        } finally {
            setBusyId(null);
        }
    };

    const enterAs = async (as, userId = null, label = '', tenantArg = null) => {
        const t = tenantArg || pick?.tenant;
        if (!t) return;
        const key = `${as}_${userId || ''}`;
        setEntering(key);
        try {
            const res = await api.post(`/auth/impersonate/${t.id}`, { as, user_id: userId });
            impersonate(res.data.token, res.data.user);
            toast.success(`${t.name}: ${label || 'admin'} kimi daxil olundu.`);
            setPick(null);
            navigate(res.data.redirect || '/admin/dashboard');
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Daxil olmaq alınmadı.');
        } finally {
            setEntering('');
        }
    };

    const q = search.trim().toLowerCase();
    const filtered = tenants.filter(t =>
        !q ||
        String(t.name || '').toLowerCase().includes(q) ||
        String(t.username_new_system || t.username || '').toLowerCase().includes(q)
    );

    return (
        <SuperadminLayout
            title="Tenantlara giriş"
            subtitle="Bir kliklə restoranın admin panelinə daxil ol (parol lazım deyil)"
        >
            <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="relative w-full sm:max-w-md">
                    <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input
                        type="text"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Restoran adı və ya istifadəçi adı"
                        className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm text-slate-800 shadow-sm transition placeholder:text-slate-400 focus:border-sky-400 focus:outline-none focus:ring-4 focus:ring-sky-100"
                    />
                </div>
                {!loading && (
                    <span className="text-xs text-slate-500">{filtered.length} restoran</span>
                )}
            </div>

            {loading ? (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                    {[0, 1, 2, 3, 4, 5].map(i => <div key={i} className="h-[76px] animate-pulse rounded-2xl border border-slate-200/70 bg-white" />)}
                </div>
            ) : filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-center">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                        <Store className="h-6 w-6" />
                    </div>
                    <div className="mt-3 text-sm font-semibold text-slate-700">Restoran tapılmadı</div>
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                    {filtered.map(t => (
                        <div key={t.id} className={`group flex items-center gap-3 rounded-2xl border border-slate-200/80 bg-white p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-shadow hover:shadow-lg hover:shadow-slate-900/5 ${t.active ? '' : 'opacity-70'}`}>
                            <div className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl text-sm font-bold text-white ${t.active ? 'bg-gradient-to-br from-sky-500 to-indigo-500' : 'bg-slate-300'}`}>
                                {initials(t.name)}
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                    <span className="truncate text-sm font-semibold text-slate-900">{t.name}</span>
                                    <span className={`h-1.5 w-1.5 flex-shrink-0 rounded-full ${t.active ? 'bg-emerald-500' : 'bg-slate-300'}`} title={t.active ? 'Aktiv' : 'Deaktiv'} />
                                </div>
                                <div className="mt-0.5 flex items-center gap-1.5 text-xs text-slate-400">
                                    <span className="truncate font-mono">@{t.username_new_system || t.username || '?'}</span>
                                    <span className={`flex-shrink-0 rounded-full px-1.5 py-px text-[10px] font-semibold ${PLAN_TONE[t.plan] || PLAN_TONE.free}`}>
                                        {PLAN_LABEL[t.plan] || 'Pulsuz'}
                                    </span>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => enter(t)}
                                disabled={busyId === t.id}
                                className="flex flex-shrink-0 items-center gap-1.5 rounded-xl bg-slate-900 px-3.5 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#0090bb] disabled:opacity-50"
                            >
                                {busyId === t.id
                                    ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Gözləyin</>
                                    : <><LogIn className="h-3.5 w-3.5" /> Daxil ol</>}
                            </button>
                        </div>
                    ))}
                </div>
            )}
            {/* Giriş növü seçimi */}
            {pick && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" onMouseDown={() => !entering && setPick(null)}>
                    <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl" onMouseDown={(e) => e.stopPropagation()}>
                        <div className="mb-4 flex items-start justify-between gap-3">
                            <div>
                                <div className="text-base font-bold text-slate-900">{pick.tenant.name}</div>
                                <div className="text-xs text-slate-500">Hansı kimi daxil olursan?</div>
                            </div>
                            <button onClick={() => setPick(null)} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100"><X className="h-4 w-4" /></button>
                        </div>

                        <div className="space-y-4">
                            <AsButton icon={<Crown className="h-4 w-4" />} tone="from-sky-500 to-indigo-500" title="Admin" sub="Admin paneli"
                                busy={entering === 'admin_'} onClick={() => enterAs('admin', null, 'admin')} />

                            <div>
                                <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Kassir</div>
                                {pick.kassirs.length === 0 ? (
                                    <div className="rounded-xl border border-dashed border-slate-200 px-3 py-2.5 text-xs text-slate-400">Kassir hesabı yoxdur{pick.tenant.plan === 'free' ? ' (pulsuz paket)' : ''}.</div>
                                ) : (
                                    <div className="space-y-1.5">
                                        {pick.kassirs.map((k) => (
                                            <AsButton key={k.id} icon={<Wallet className="h-4 w-4" />} tone="from-emerald-500 to-teal-500" title={k.name || 'Kassir'} sub="Kassir paneli"
                                                busy={entering === `kassir_${k.id}`} onClick={() => enterAs('kassir', k.id, `kassir (${k.name})`)} />
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div>
                                <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Mətbəx</div>
                                {pick.kitchens.length === 0 ? (
                                    <div className="rounded-xl border border-dashed border-slate-200 px-3 py-2.5 text-xs text-slate-400">Mətbəx hesabı yoxdur.</div>
                                ) : (
                                    <div className="space-y-1.5">
                                        {pick.kitchens.map((k) => (
                                            <AsButton key={k.id} icon={<ChefHat className="h-4 w-4" />} tone="from-amber-500 to-orange-500" title={k.name || 'Mətbəx'} sub={k.printer ? `Printer: ${k.printer}` : 'Mətbəx ekranı'}
                                                busy={entering === `kitchen_${k.id}`} onClick={() => enterAs('kitchen', k.id, `mətbəx (${k.name})`)} />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </SuperadminLayout>
    );
}

function AsButton({ icon, tone, title, sub, busy, onClick }) {
    return (
        <button type="button" onClick={onClick} disabled={busy}
            className="flex w-full items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-left transition hover:border-sky-300 hover:bg-sky-50/40 disabled:opacity-60">
            <span className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br text-white ${tone}`}>{icon}</span>
            <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold text-slate-900">{title}</span>
                <span className="block truncate text-[11px] text-slate-500">{sub}</span>
            </span>
            {busy ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" /> : <LogIn className="h-4 w-4 text-slate-400" />}
        </button>
    );
}
